<template>
    <div>
        <h1 class="font-[700] text-3xl text-center text-[#414956] dark:text-white mb-[3vh]">
            {{ $t('pages.forgotPassword.step2.checkEmail') }}
        </h1>
        <div class="bg-[#E0F2FE] dark:bg-gray-700 rounded-lg px-10 pt-14 pb-10 mb-[3vh] w-fit mx-auto">
            <p class="text-[#414956] dark:text-white text-center text-xl font-bold mb-[2vh] text-nowrap">
                {{ $t('pages.forgotPassword.step2.enterCode') }} {{ props.email }}
            </p>
            <!-- input -->
            <div class="flex justify-center gap-2 mb-4" role="group" aria-label="驗證碼輸入">
                <input v-for="(digit, idx) in 6" :key="idx" type="text" :aria-label="`第 ${idx + 1} 位驗證碼`" maxlength="1"
                    autocomplete="one-time-code" inputmode="numeric" pattern="[0-9]*" :disabled="isLoading" :class="[
                        'w-12 h-12 text-center border border-gray-300 rounded focus:outline-none focus:border-[#06B6D4] text-2xl bg-white dark:bg-gray-900',
                        { 'opacity-50': isLoading }
                    ]" v-model="codeArr[idx]" @input="onInput(idx, $event)"
                    @keydown.backspace="onBackspace(idx, $event)" @paste="handlePaste"
                    :ref="(el) => { if (el) inputRefs[idx] = el as HTMLInputElement }" />
            </div>
            <div class="h-4">
                <p v-if="errorMessage" class="text-red-500 text-center mb-2 text-sm">{{ errorMessage }}</p>
                <p v-if="isLoading" class="text-gray-500 text-center mb-2 text-sm">{{ $t('pages.forgotPassword.step2.validating') }}</p>
            </div>
        </div>

        <p class="text-center text-base font-bold text-[#414956] dark:text-white">
            {{ $t('pages.forgotPassword.step2.noCode') }}
            <span v-if="countDown === 0" class="text-[#06B6D4] hover:underline font-bold cursor-pointer ml-1"
                @click="resendCode">
                {{ $t('pages.forgotPassword.step2.resend') }}
            </span>
            <span v-else>
                {{ $t('pages.forgotPassword.step2.resendIn') }}
                {{ countDown }}
                s
            </span>
        </p>
    </div>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { sendRegisterCodeApi, validateCodeApi } from '~/api/auth';
import type { ApiErrorWithStatus } from '~/types/api';

const { t } = useI18n();
const emit = defineEmits(['toStep3']);

const props = defineProps<{ email: string }>();

const code = ref('');
const errorMessage = ref('');
const isLoading = ref(false);
const codeArr = ref<string[]>(['', '', '', '', '', '']);
const inputRefs = ref<HTMLInputElement[]>([]);

// 監聽驗證碼變化
watch(codeArr.value, (val) => {
  code.value = val.join('');
  if (code.value.length === 6) {
    validateCode();
  } else {
    errorMessage.value = '';
  }
});

// 驗證輸入是否為數字
const validateInput = (value: string) => {
  return /^[0-9]$/.test(value);
};

// 處理貼上事件
const handlePaste = (e: ClipboardEvent) => {
  e.preventDefault();
  const pastedText = e.clipboardData?.getData('text') || '';
  const numbers = pastedText.replace(/[^0-9]/g, '').split('');

  // 只取前6位數字
  numbers.slice(0, 6).forEach((num, idx) => {
    if (idx < codeArr.value.length) {
      codeArr.value[idx] = num;
    }
  });

  // 自動聚焦到最後一個輸入框
  if (numbers.length > 0) {
    nextTick(() => {
      const lastIndex = Math.min(numbers.length - 1, 5);
      inputRefs.value[lastIndex]?.focus();
    });
  }
};

// 處理輸入事件
const onInput = (idx: number, e: Event) => {
  const input = e.target as HTMLInputElement;
  const value = input.value;

  // 驗證輸入
  if (!validateInput(value)) {
    input.value = '';
    return;
  }

  // 更新當前輸入框的值
  codeArr.value[idx] = value;

  // 自動跳到下一個輸入框
  if (value && idx < 5) {
    nextTick(() => {
      const nextInput = inputRefs.value[idx + 1];
      if (nextInput) {
        nextInput.focus();
      }
    });
  }
};

// 處理退格鍵
const onBackspace = (idx: number, e: KeyboardEvent) => {
  if (e.key === 'Backspace' && !codeArr.value[idx] && idx > 0) {
    nextTick(() => {
      const prevInput = inputRefs.value[idx - 1];
      if (prevInput) {
        prevInput.focus();
      }
    });
  }
};

// 驗證碼驗證
const validateCode = async () => {
  try {
    isLoading.value = true;
    errorMessage.value = '';
    await validateCodeApi(code.value, props.email);
    emit('toStep3', code.value);
  } catch (error) {
    const apiError = error as ApiErrorWithStatus | Error;
    if ('message' in apiError && apiError.message) {
      errorMessage.value = apiError.message;
    } else {
      errorMessage.value = t('pages.forgotPassword.step2.invalidCode');
    }
    // 清空輸入框
    codeArr.value = ['', '', '', '', '', ''];
    // 聚焦第一個輸入框
    inputRefs.value[0]?.focus();
  } finally {
    isLoading.value = false;
  }
};

// 倒數計時
const countDown = ref(0);

// 重新發送驗證碼
const resendCode = () => {
  sendRegisterCodeApi(props.email);
  // count down
  countDown.value = 30;
  const timer = setInterval(() => {
    countDown.value--;
  }, 1000);

  setTimeout(() => {
    clearInterval(timer);
  }, 30000);
};
</script>